import React, { useEffect, useMemo, useState } from 'react';
import { useStyletron } from 'baseui';
import { Button, KIND as ButtonKind } from 'baseui/button';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { ModalBody, ModalFooter, ModalHeader } from 'baseui/modal';
import type { GenericAnnotationT } from '../../../../../common/types/types';
import type { OutputRelationshipT } from '../../../../../modernization-common/relationships';
import { DELETE_MODAL_MESSAGES } from '../../../../labelling/components/side-panel/conts';
import { TimeDisplayMode } from '../../../../types';
import { getFrameToTime, getTimeToFrame } from '../../../../utils/frame-time';
import { getDeleteLinkedRelationshipsMessage, isOverlappingFrameRange } from '../utils';
type PropsT = {
  annotationId: string;
  isVideoTask: boolean;
  allAnnotationMap: {
    [annotationId: string]: {
      annotation: GenericAnnotationT;
      startFrameIndex: number;
      endFrameIndex: number;
    };
  };
  timeDisplayMode: TimeDisplayMode;
  maxFrames: number;
  frameRate: number | null | undefined;
  onSubmit?: (startFrameCount: number, endFrameCount: number, relationshipIds?: string[]) => Promise<void> | void;
  onCancel?: () => void;
  associatedRelationships?: OutputRelationshipT[];
};
export function DeleteModalContent({
  onCancel,
  annotationId,
  isVideoTask,
  allAnnotationMap,
  onSubmit,
  timeDisplayMode,
  maxFrames,
  frameRate,
  associatedRelationships
}: PropsT) {
  const [css, theme] = useStyletron();
  const annotationInfo = allAnnotationMap[annotationId];
  const defaultStartFrame = annotationInfo?.startFrameIndex ?? 0;
  const defaultStartFrameTime = getFrameToTime({
    frame: defaultStartFrame,
    maxFrames,
    frameRate
  });
  const defaultStartInputValue = timeDisplayMode === TimeDisplayMode.FRAMES ? defaultStartFrame + 1 : defaultStartFrameTime;
  const defaultEndFrame = annotationInfo?.endFrameIndex ?? 0;
  const defaultEndFrameTime = getFrameToTime({
    frame: defaultEndFrame,
    maxFrames,
    frameRate
  });
  const defaultEndInputValue = timeDisplayMode === TimeDisplayMode.FRAMES ? defaultEndFrame + 1 : defaultEndFrameTime;
  const [startFrame, setStartFrame] = useState(defaultStartFrame);
  const [endFrame, setEndFrame] = useState(defaultEndFrame);
  const [isLoading, setIsLoading] = useState(false);
  const associatedRelationshipsForFrameRange = useMemo(() => {
    return associatedRelationships?.filter(relationship => {
      return relationship.frameRanges.some(frameRange => {
        return isOverlappingFrameRange(frameRange, {
          startFrameIndex: startFrame,
          endFrameIndex: endFrame
        });
      });
    });
  }, [associatedRelationships, startFrame, endFrame]);
  const message = getDeleteLinkedRelationshipsMessage(associatedRelationshipsForFrameRange?.length ?? 0);
  const isValueInBound = (newValue: number) => {
    if (newValue > defaultEndFrame) {
      return false;
    }
    if (newValue < defaultStartFrame) {
      return false;
    }
    return true;
  };
  const handleInputChange = (setValue: React.Dispatch<React.SetStateAction<number>>) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const trimmedValue = value.trim();
    let updatedFrameNumber;
    if (timeDisplayMode === TimeDisplayMode.FRAMES) {
      updatedFrameNumber = Number(trimmedValue) - 1;
    } else {
      const timeFrameNumber = getTimeToFrame(trimmedValue, frameRate);
      updatedFrameNumber = timeFrameNumber;
    }
    setValue(updatedFrameNumber);
  };
  const startFrameInputPlaceholder = timeDisplayMode === TimeDisplayMode.FRAMES ? 'Start frame index' : 'Start time';
  const endFrameInputPlaceholder = timeDisplayMode === TimeDisplayMode.FRAMES ? 'End frame index' : 'End time';
  const handleSubmit = async () => {
    setIsLoading(true);
    const relationshipIds = associatedRelationshipsForFrameRange?.map(r => r.id) ?? [];
    await onSubmit?.(startFrame + 1, endFrame + 1, relationshipIds);
    setIsLoading(false);
  };
  return <>
      <ModalHeader>{DELETE_MODAL_MESSAGES.TITLE}</ModalHeader>
      <ModalBody>
        {DELETE_MODAL_MESSAGES.WARNING_MESSAGE} <b>{annotationId}</b> ?
        {message && <div>{message}</div>}
        <div className={css({
        display: 'flex',
        gap: theme.sizing.scale300
      })}>
          {isVideoTask && <>
              <div className={css({
            flexGrow: 1
          })}>
                <FormControl label={() => DELETE_MODAL_MESSAGES.START_FRAME} caption={`${DELETE_MODAL_MESSAGES.MIN_VALUE} ${defaultStartInputValue}`}>
                  <Input overrides={{
                Input: {
                  props: {
                    defaultValue: String(defaultStartInputValue)
                  }
                }
              }} type="text" onChange={handleInputChange(setStartFrame)} error={!isValueInBound(startFrame)} placeholder={startFrameInputPlaceholder} />
                </FormControl>
              </div>
              <div className={css({
            flexGrow: 1
          })}>
                <FormControl label={() => DELETE_MODAL_MESSAGES.END_FRAME} caption={`${DELETE_MODAL_MESSAGES.MAX_VALUE} ${defaultEndInputValue}`}>
                  <Input overrides={{
                Input: {
                  props: {
                    defaultValue: String(defaultEndInputValue)
                  }
                }
              }} type="text" onChange={handleInputChange(setEndFrame)} error={!isValueInBound(endFrame)} placeholder={endFrameInputPlaceholder} />
                </FormControl>
              </div>
            </>}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button kind={ButtonKind.tertiary} onClick={onCancel} disabled={isLoading}>
          {DELETE_MODAL_MESSAGES.CANCEL}
        </Button>
        <Button disabled={isLoading || !isValueInBound(startFrame) || !isValueInBound(endFrame)} isLoading={isLoading} onClick={handleSubmit}>
          {DELETE_MODAL_MESSAGES.DELETE}
        </Button>
      </ModalFooter>
    </>;
}